/**
 * FAN ENJOYER — Current Manipulator (doc 05 §5). 1×1, 45 alloy, 150 HP, slow
 * field r4 at 30%, 3 watts constant, and no damage whatsoever.
 *
 * "Blows. Professionally."
 *
 * Silhouette family: DUCT RING. A standing hollow ring on a stubby pedestal —
 * the only tower with a HOLE through its middle, which is what carries the
 * black-shape test against every solid mast and box in the class. The hole is
 * also the whole design: you can see the current going through it.
 *
 * Signature: warm red hub lamp inside the ring.
 *
 * Upgrade motifs (doc 05 3×3):
 *   A · MOLASSES MODE   vane fins thicken the ring until it is a turbine
 *   B · LEAF BLOWER     a nozzle grows out of the front, ending in a snout
 *   C · KITCHEN SINK    intake scoops on the back, ending in a vortex cage
 */

import { tiersOf, type BuildContext, type EntityDraw } from '../entity.ts';
import { rampFor, sigRamp, signatureColor, SIGNATURE_TOWER } from '../materials.ts';
import { isoCylinder, isoDiamondRim, isoPrism, linePx, type EntityCanvas } from '../../render/primitives.ts';

/** The ring, drawn as a stack of arcs so the hole survives the outline pass. */
function ring(c: EntityCanvas, cx: number, cy: number, rx: number, ry: number, colour: number): void {
  for (let a = 0; a < 32; a++) {
    const t = (a / 32) * Math.PI * 2;
    c.s.px(cx + Math.round(Math.cos(t) * rx), cy + Math.round(Math.sin(t) * ry), colour);
  }
}

export const drawFan: EntityDraw = (c: EntityCanvas, ctx: BuildContext): void => {
  const r = rampFor(ctx.depth);
  const sig = sigRamp(SIGNATURE_TOWER);
  const lamp = signatureColor(sig, ctx.state);
  const up = tiersOf(ctx);
  const running = ctx.state === 'firing' || ctx.state === 'tracking';

  // --- pedestal --------------------------------------------------------------
  isoPrism(c, { n: 1, inset: 3, height: 3, ramp: r });
  isoDiamondRim(c.s, c.ax, c.ay - 3, 6, r.edge);
  const postTop = isoCylinder(c, { rx: 4, height: 3, lift: 3, ramp: r });

  // --- the ring: two concentric rims with a hub bar --------------------------
  const cy = postTop - 5;
  const rx = 11 + (up.a >= 2 ? 1 : 0);
  const ry = 7 + (up.a >= 2 ? 1 : 0);
  ring(c, c.ax, cy, rx, ry, r.edge);
  ring(c, c.ax, cy, rx - 2, ry - 2, r.left);
  c.s.fillRect(c.ax - 1, cy - ry + 2, 3, 2, r.left); // top mount
  c.s.fillRect(c.ax - 1, cy + ry - 3, 3, 3, r.left); // stem into the post

  // hub: the only solid thing in the middle, so the hole still reads
  c.s.fillRect(c.ax - 2, cy - 2, 5, 4, r.left);
  c.s.fillRect(c.ax - 2, cy - 2, 5, 1, r.edge);

  // a running fan HAS blades; a stopped one is an empty hoop. That is the
  // whole read of the state at gameplay zoom, so it is worth real pixels.
  if (running) {
    const spin = Math.round(ctx.phase * 2) % 2;
    const spokes: Array<[number, number]> = spin === 0
      ? [[-7, -3], [7, 3], [-7, 3], [7, -3]]
      : [[0, -5], [0, 5], [-9, 0], [9, 0]];
    for (const [dx, dy] of spokes) {
      linePx(c.s, c.ax, cy, c.ax + dx, cy + dy, r.left, 2);
      c.s.px(c.ax + dx, cy + dy, r.edge);
    }
  }

  // --- A · MOLASSES MODE: vanes fill the ring -------------------------------
  if (up.a >= 1) {
    const spin = running ? Math.round(ctx.phase * 2) % 2 : 0;
    for (const [dx, dy] of (spin === 0
      ? [
          [-5, 0],
          [5, 0],
        ]
      : [
          [0, -4],
          [0, 4],
        ]) as Array<[number, number]>) {
      c.s.fillRect(c.ax + dx - 1, cy + dy - 1, 3, 3, r.left);
    }
  }
  if (up.a >= 3) {
    // "Speed Limit: No": a full turbine face — six blades, always visible
    for (const [dx, dy] of [
      [-6, -2],
      [-4, 3],
      [0, -5],
      [0, 5],
      [4, -3],
      [6, 2],
    ] as Array<[number, number]>) {
      c.s.fillRect(c.ax + dx - 1, cy + dy - 1, 2, 2, r.edge);
    }
  }

  // --- B · LEAF BLOWER: a nozzle out the front ------------------------------
  if (up.b >= 1) {
    c.s.fillRect(c.ax + rx - 2, cy - 2, 5, 5, r.left);
    c.s.fillRect(c.ax + rx - 2, cy - 2, 5, 1, r.edge);
  }
  if (up.b >= 2) c.s.fillRect(c.ax + rx + 2, cy - 3, 3, 7, r.left);
  if (up.b >= 3) {
    // "Respectfully, Leave": the snout is longer than the ring is wide
    c.s.fillRect(c.ax + rx + 4, cy - 4, 4, 9, r.left);
    c.s.fillRect(c.ax + rx + 4, cy - 4, 4, 1, r.edge);
    c.s.fillRect(c.ax + rx + 7, cy - 2, 2, 5, r.edge);
  }

  // --- C · KITCHEN SINK VORTEX: intakes on the back -------------------------
  if (up.c >= 1) c.s.fillRect(c.ax - rx - 2, cy - 2, 4, 5, r.left);
  if (up.c >= 2) {
    c.s.fillRect(c.ax - rx - 4, cy - 4, 3, 9, r.left);
    c.s.fillRect(c.ax - rx - 4, cy - 4, 3, 1, r.edge);
  }
  if (up.c >= 3) {
    // "Krill Blender Adapter": a cage that swallows the whole back half
    ring(c, c.ax - 5, cy, rx - 1, ry - 1, r.left);
    for (const dy of [-4, 0, 4]) c.s.fillRect(c.ax - rx - 6, cy + dy - 1, 3, 2, r.edge);
  }

  // --- signature + states ----------------------------------------------------
  if (lamp !== null) c.s.fillRect(c.ax - 1, cy - 1, 2, 2, lamp);
  if (ctx.state === 'firing') {
    // the current is visible: two wind ticks leaving the ring
    const k = Math.round(ctx.phase * 2) % 2;
    c.s.px(c.ax + rx + 2 + k, cy - 2, sig.halo);
    c.s.px(c.ax + rx + 2 + k, cy + 2, sig.halo);
    c.s.fillRect(c.ax - 2, cy - 2, 5, 4, sig.hot, 'set');
  } else if (ctx.state === 'tracking') {
    c.s.fillRect(c.ax - 1, cy - 1, 2, 2, sig.on, 'set');
  }
  if (ctx.state === 'dry') {
    // no watts: the blades stop and the hub goes dark
    c.s.fillRect(c.ax - 2, cy - 2, 5, 4, r.deep, 'set');
    c.s.fillRect(c.ax - 4, c.ay - 4, 9, 1, r.deep);
  }
  if (ctx.state === 'selected') isoDiamondRim(c.s, c.ax, c.ay - 3, 6, r.edge);
};
